import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { EditOutlined, FilterOutlined, PlusOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Empty, Input, notification, Tag, Card } from 'antd';
import DateRange from '../../../components/SalesOrders/DropDowns/DateRange';
import { QuoteListPrice } from '../../../components/QuoteListPrice/QuoteListPrice';
import { observer } from 'mobx-react';
import * as _ from 'lodash';

import dayjs from 'dayjs';
import UIStore from '../../../stores/UIStore';
import UserStore from '../../../stores/UserStore';
import Verify from '../../../components/Verify';
import { ColumnProps } from 'antd/es/table';
import { QuoteProps } from '../../../types/types';
import { TablePaginationConfig, TableProps } from 'antd/es/table';
import { CustomResponsiveTable } from '../../../components/common/SimpleResponsiveTable';
import QuoteStore from '../../../stores/QuoteStore';

import { ErrorRender } from '@partsbadger/library';
import { ListPageHeader, StatusChip } from '../../../components/ListPageHeader';
import { buildQuoteQueryParams, normalizeDateRange, QUOTE_CREATED_DATE_COLUMN_TITLE } from './helpers';

const QUOTE_STATUS_CHIPS: StatusChip[] = [
    { label: 'All', value: undefined },
    { label: 'Draft', value: 'Draft' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' },
];

const STATUS_COLOR_MAP: Record<string, string> = {
    Draft: 'gold',
    'In Progress': 'blue',
    Completed: 'green',
    Cancelled: 'red',
};

const isUnfinishedRevision = (record: QuoteProps) =>
    record.customer_status === 'In Progress' && record.duplicated_from !== null;

const QuotesList: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<string>();
    const [customerStatus, setCustomerStatus] = useState<string | undefined>();
    const [dates, setDates] = useState<[string, string]>();
    const [completedDates, setCompletedDates] = useState<[string, string]>();
    const history = useHistory();
    const location = useLocation();
    const debouncedSearchRef = useRef(
        _.debounce((value: string) => {
            setSearch(value || undefined);
        }, 400)
    );

    useEffect(() => {
        return () => debouncedSearchRef.current.cancel();
    }, []);

    const columns: ColumnProps<QuoteProps>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            width: 280,
            filterIcon: <SearchOutlined style={{ color: search ? '#c00' : '#bfbfbf' }} />,
            render: (_text, record) => {
                return (
                    <div>
                        {record.name + '  '}
                        {record?.type === 'Requote' && (
                            <Tag color="#b1ed98" style={{ fontSize: '0.75em' }}>
                                Requote
                            </Tag>
                        )}
                        {record?.customer_supplied_material && (
                            <Tag color="#b6cfab" style={{ fontSize: '0.75em' }}>
                                Customer Material
                            </Tag>
                        )}
                        {record?.parts.find(p => p.itar) && (
                            <Tag color="#9fbf91" style={{ fontSize: '0.75em' }}>
                                ITAR
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: QUOTE_CREATED_DATE_COLUMN_TITLE,
            dataIndex: 'created_time',
            width: 180,
            filteredValue: dates ?? null,
            filterDropdown: (
                <DateRange
                    current={dates}
                    search={(min, max) => {
                        setDates(normalizeDateRange(min, max));
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
            sorter: true,
            render: (_text, record) => {
                return (
                    <div>
                        <div>
                            {record.created_time
                                ? dayjs(record.created_time).format('YYYY-MM-DD')
                                : record.created_time}
                        </div>
                        <div className={'italic'}>By {record.contact?.fullname}</div>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'customer_status',
            width: 140,
            render: (_text, record) => {
                const cs = record?.customer_status;
                if (cs === 'Completed' && !record?.grand_total) {
                    return <Tag>No Quote</Tag>;
                }
                return <Tag color={STATUS_COLOR_MAP[cs] ?? 'default'}>{cs}</Tag>;
            },
        },
        {
            title: 'Completed Date',
            dataIndex: 'completed_at',
            width: 150,
            filteredValue: completedDates ?? null,
            filterDropdown: (
                <DateRange
                    current={completedDates}
                    search={(min, max) => {
                        setCompletedDates(normalizeDateRange(min, max));
                    }}
                />
            ),
            filterIcon: <FilterOutlined />,
            sorter: true,
            render: (_text, record) => {
                return <div>{record.completed_at ? dayjs(record.completed_at).format('YYYY-MM-DD') : '-'}</div>;
            },
        },
        {
            title: 'Grand Total',
            dataIndex: 'grand_total',
            width: 140,
            render: (_text, record) => {
                if (record?.customer_status === 'Completed' && !record?.grand_total) {
                    return 'No Quote';
                }
                return <QuoteListPrice value={record.grand_total} completed={record.completed} quote_id={record.id} />;
            },
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            width: 180,
            render: (_text, record) => {
                const isDraft = record.customer_status === 'Draft';
                const shouldContinueCheckout = isUnfinishedRevision(record);

                return (
                    <div className="flex-col border-transparent border-0 text-left break-all">
                        <div>
                            <Button
                                type={'link'}
                                className="text-red-600"
                                data-testid={
                                    isDraft
                                        ? 'quote-row-resume-action'
                                        : shouldContinueCheckout
                                        ? 'quote-row-continue-checkout-action'
                                        : 'quote-row-view-action'
                                }
                                icon={isDraft || shouldContinueCheckout ? <EditOutlined /> : <EyeOutlined />}
                            >
                                {isDraft
                                    ? 'Resume Submission'
                                    : shouldContinueCheckout
                                    ? 'Continue Checkout'
                                    : 'View Details'}
                            </Button>
                        </div>
                    </div>
                );
            },
        },
    ];

    const handleTableChange: TableProps<QuoteProps>['onChange'] = (newPagination, _filters, sorter) => {
        QuoteStore.setPagination(newPagination);
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        fetchQuotes(
            newPagination,
            s && typeof s === 'object' && 'field' in s
                ? { field: s.field as string, order: s.order as string }
                : undefined
        );
    };

    const fetchQuotes = (newPagination?: TablePaginationConfig, sorter?: { field?: string; order?: string }) => {
        setLoading(true);
        const queryArr = buildQuoteQueryParams({
            pagination: newPagination,
            search,
            customerStatus,
            createdDates: dates,
            completedDates,
            sorter,
        });

        QuoteStore.getQuotes(queryArr)
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'topRight',
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        UIStore.setSelectedKey(['quotes']);
        UIStore.setDocumentTitle('Quotes');

        if (UserStore.isVerified) {
            const nextPagination: TablePaginationConfig = {
                ...QuoteStore.pagination,
                current: 1,
                pageSize: QuoteStore?.pagination?.pageSize ?? 20,
            };
            QuoteStore.setPagination(nextPagination);
            fetchQuotes(nextPagination, undefined);
        }
    }, [search, customerStatus, dates, completedDates]);

    const emptyTable = () => {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span style={{ color: '#000' }}>Place a quote with PartsBadger!</span>}
            >
                <Button type="primary">
                    <Link to="/my-account/quotes/new-quote">Submit an RFQ</Link>
                </Button>
            </Empty>
        );
    };

    const handleRowClick = (record: QuoteProps) => {
        if (record.customer_status === 'Draft') {
            history.push(`/my-account/quotes/${record.id}/continue/`);
            return;
        }
        if (isUnfinishedRevision(record)) {
            history.push(`/my-account/quotes/${record.id}/order/`);
            return;
        }

        history.push(`${location.pathname}/${record.id}`);
    };

    const clearAllFilters = () => {
        setSearch(undefined);
        setCustomerStatus(undefined);
        setDates(undefined);
        setCompletedDates(undefined);
    };

    if (!UserStore.isVerified) {
        return <Verify />;
    }

    return (
        <>
            <ListPageHeader
                title="Quotes"
                subtitle="Manage and track all your quotes"
                action={
                    <Link to="/my-account/quotes/new-quote">
                        <Button type="primary" size="large" icon={<PlusOutlined />} className="w-full sm:w-auto">
                            New Quote
                        </Button>
                    </Link>
                }
                searchNode={
                    <Input
                        allowClear
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Search by quote number or part name..."
                        size="large"
                        className="w-full"
                        onChange={e => debouncedSearchRef.current(e.target.value)}
                    />
                }
                chips={QUOTE_STATUS_CHIPS}
                activeChip={customerStatus}
                onChipChange={setCustomerStatus}
                dates={dates}
                onDatesChange={setDates}
                showDateFilter={false}
                totalCount={QuoteStore.pagination?.total ?? 0}
                entityName="quotes"
                hasActiveFilters={!!(customerStatus || search || dates || completedDates)}
                onClearFilters={clearAllFilters}
            />

            <div className="container">
                <Card>
                    <CustomResponsiveTable
                        columns={columns}
                        onRow={record => {
                            return {
                                'data-testid': 'quote-row',
                                'data-quote-id': String(record.id),
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' },
                            };
                        }}
                        loading={loading}
                        data={QuoteStore.quotes}
                        pagination={QuoteStore.pagination}
                        onChange={handleTableChange}
                        emptyTable={emptyTable}
                    />
                </Card>
            </div>
        </>
    );
};

export default observer(QuotesList);
